import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
  sortAscIcon,
  sortDescIcon,
  sortIcon,
} from '../../icons'
import { isAnyElementInArray } from '../../utils/arrayUtil'
import {
  getGroupQrTags,
  getQrTagLatestEvent,
  type QrTagSort,
  type QrTagSortOrder,
} from '../../modules/QrTag'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import Loading from '../common/Loading'
import QrTagsListItem from './QrTagsListItem'
import DetailQrTagModal from './DetailQrTagModal'
import QrTagEventScannerModal from './QrTagEventScannerModal'
import './OverviewItemList.css'
// import { GROUP_QR_PRINT, QR_TAG_EVENT_RECORDER } from '../../constants/Paths'
import { GROUP_QR_PRINT } from '../../constants/Paths'

type Props = {
  group: IGroup
  keyword?: string
  latestEventType?: string
}

const QrTagsList = ({
  group,
  keyword = '',
  latestEventType = '',
}: Props) => {
  const dispatch = useAppDispatch()
  const eventTypesEntry = useAppSelector((state) => state.qrTagEventTypes.entries[group.id])
  const eventTypes = useMemo(() => eventTypesEntry?.values ?? [], [eventTypesEntry])
  const [qrTags, setQrTags] = useState<IQrTag[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedTag, setSelectedTag] = useState<IQrTag | null>(null)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [sort, setSort] = useState<QrTagSort | null>(null)
  const [order, setOrder] = useState<QrTagSortOrder>('desc')
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  useEffect(() => {
    dispatch(fetchGroupEventTypes({ groupId: group.id }))
  }, [dispatch, group.id])

  const attachLatestEvent = useCallback(async (tags: IQrTag[]): Promise<IQrTag[]> => {
    const results = await Promise.all(tags.map(async (tag) => {
      try {
        const lastEvent = await getQrTagLatestEvent(group.id, tag.id)
        return {
          ...tag,
          last_event: lastEvent,
        }
      } catch (err) {
        console.error('Failed to fetch latest event for QR tag', tag.id, err)
        return {
          ...tag,
          last_event: null,
        }
      }
    }))

    return results
  }, [group.id])

  const fetchQrTags = useCallback(async () => {
    if (!isMountedRef.current) return

    try {
      setLoading(true)
      const tags = await getGroupQrTags(group.id, {
        keyword,
        sort: sort ?? undefined,
        order,
        latest_event_type: latestEventType || undefined,
      })
      const hydratedTags = await attachLatestEvent(tags)
      if (!isMountedRef.current) return
      setQrTags(hydratedTags)
      setError(null)
    } catch (err) {
      if (!isMountedRef.current) return
      console.error('Failed to load QR tags', err)
      setError('QRタグの読み込みに失敗しました')
      setQrTags([])
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [attachLatestEvent, group.id, keyword, latestEventType, order, sort])

  useEffect(() => {
    fetchQrTags()
  }, [fetchQrTags])

  const emptyRowText = useMemo(() => {
    if (loading) return 'QRタグを読み込み中...'
    if (error) return error
    return 'このグループにはQRタグがありません。'
  }, [loading, error])

  // const recorderPagePath = useMemo(
  //   () => QR_TAG_EVENT_RECORDER.replace(':group_id', String(group.id)),
  //   [group.id],
  // )

  const groupQrPrintPagePath = useMemo(
    () => GROUP_QR_PRINT.replace(':group_id', String(group.id)),
    [group.id],
  )

  const eventTypeColors = useMemo(() => (
    new Map(eventTypes.map((eventType) => [eventType.name, eventType.color]))
  ), [eventTypes])

  const handleScanCompleted = async () => {
    setIsScannerOpen(false)
    await fetchQrTags()
  }

  const onClickSortHeader = (key: QrTagSort) => {
    if (sort !== key) {
      setSort(key)
      setOrder('asc')
      return
    }

    setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const getSortHeaderIcon = (key: QrTagSort) => {
    if (sort !== key) return sortIcon
    return order === 'asc' ? sortAscIcon : sortDescIcon
  }

  const cursor = 'pointer'

  return (
    <div className="overview-list" data-testid="qr-tags-list">
      <Loading show={loading} />
      <div style={{
        display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', marginTop: '24px', gap: '8px',
      }}
      >
        {/* <button
          type="button"
          className="btn btn-primary"
          onClick={() => setIsScannerOpen(true)}
          data-testid="qr-tag-open-scanner-button"
        >
          QRタグイベントを記録する
        </button> */}
        <Link
          to={groupQrPrintPagePath}
          className="btn btn-outline-secondary"
        >
          グループQRを印刷する
        </Link>
        {/* <Link
          to={recorderPagePath}
          className="btn btn-outline-secondary hidden"
        >
          専用ページを開く
        </Link> */}
      </div>
      <table className="table">
        <thead className="header">
          <tr>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onClickSortHeader('name')}
                data-testid="qr-tags-header-sort-name"
              >
                タグ名
                <img alt="sort" src={getSortHeaderIcon('name')} />
              </div>
            </th>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onClickSortHeader('description')}
                data-testid="qr-tags-header-sort-description"
              >
                説明
                <img alt="sort" src={getSortHeaderIcon('description')} />
              </div>
            </th>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onClickSortHeader('qr_code_number')}
                data-testid="qr-tags-header-sort-qr-code-number"
              >
                QRコード番号
                <img alt="sort" src={getSortHeaderIcon('qr_code_number')} />
              </div>
            </th>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onClickSortHeader('latest_event')}
                data-testid="qr-tags-header-sort-latest-event"
              >
                最新イベント
                <img alt="sort" src={getSortHeaderIcon('latest_event')} />
              </div>
            </th>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onClickSortHeader('latest_event_occurred_at')}
                data-testid="qr-tags-header-sort-latest-event-occurred-at"
              >
                時刻
                <img alt="sort" src={getSortHeaderIcon('latest_event_occurred_at')} />
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="list">
          {
            !qrTags || !isAnyElementInArray(qrTags)
              ? (
                <tr>
                  <td colSpan={5} className="list-row">{emptyRowText}</td>
                </tr>
              )
              : qrTags.map((tag) => (
                <QrTagsListItem
                  key={tag.id}
                  qrTag={tag}
                  onClick={() => setSelectedTag(tag)}
                  eventTypeColors={eventTypeColors}
                />
              ))
          }
        </tbody>
      </table>
      {selectedTag && (
        <DetailQrTagModal
          modalProps={{
            qrTag: selectedTag,
            group,
            refreshList: fetchQrTags,
          }}
          closeModalFunction={() => setSelectedTag(null)}
        />
      )}
      {isScannerOpen && (
        <QrTagEventScannerModal
          modalProps={{
            group,
            onCompleted: handleScanCompleted,
          }}
          closeModalFunction={() => setIsScannerOpen(false)}
        />
      )}
    </div>
  )
}

export default QrTagsList
